<script lang="ts">
  import { t } from '$core/i18n';
  import type { Snippet } from 'svelte';
  import { Icon } from '$ui/primitives/Icon';

  let {
    open = $bindable(false),
    onClose,
    children
  }: {
    open?: boolean;
    onClose?: () => void;
    children: Snippet;
  } = $props();

  let panelEl: HTMLElement | undefined = $state();
  let triggerEl: HTMLElement | null = null;
  let prevBodyOverflow = '';

  function requestClose() {
    open = false;
    onClose?.();
  }

  function onKeydown(e: KeyboardEvent) {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      requestClose();
    }
  }

  $effect(() => {
    if (open) {
      triggerEl = document.activeElement as HTMLElement | null;
      prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      window.setTimeout(() => panelEl?.focus(), 20);
    } else if (triggerEl) {
      document.body.style.overflow = prevBodyOverflow;
      if (document.body.contains(triggerEl)) {
        window.setTimeout(() => triggerEl?.focus(), 20);
      }
      triggerEl = null;
    }

    // Defensive cleanup on unmount: if the drawer is destroyed while open,
    // release the overflow lock so the page stays scrollable.
    return () => {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = prevBodyOverflow;
      }
    };
  });

  function onBackdropPointerDown(e: PointerEvent) {
    if (e.target === e.currentTarget) requestClose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if open}
  <div
    class="backdrop"
    role="presentation"
    onpointerdown={onBackdropPointerDown}
  >
    <div
      class="panel"
      role="dialog"
      aria-modal="true"
      aria-label={t('shell.mobile.openMenu')}
      tabindex="-1"
      bind:this={panelEl}
    >
      <button
        type="button"
        class="close"
        aria-label={t('shell.mobile.closeMenu')}
        onclick={requestClose}
      >
        <Icon name="x" size={20} />
      </button>
      {@render children()}
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    background: var(--color-scrim);
    animation: fade-in var(--dur-slow) var(--ease-out);
  }

  .panel {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(320px, 85vw);
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border-right: var(--border-width) solid var(--color-border);
    box-shadow: var(--shadow-md);
    animation: slide-in var(--dur-slow) var(--ease-out);
    overflow-y: auto;
  }

  .panel:focus {
    outline: none;
  }

  .close {
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    z-index: 1;
  }

  .close:hover {
    color: var(--color-text-primary);
    background: var(--color-surface-hover);
  }

  .close:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slide-in {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
</style>